/**
 * Imported:
 * - From: https://usekit.kiron.dev/docs/hooks/use-autosize-textarea
 * - On: 05/07/2025
 *
 * Changed:
 * - Updated wildcard React import to individual imports.
 * - Added default `borderWidth` value of 1.
 */
import { DependencyList, RefObject, useLayoutEffect, useRef } from 'react';

interface Props {
  ref: RefObject<HTMLTextAreaElement | null>;
  maxHeight?: number;
  borderWidth?: number;
  dependencies: DependencyList;
}

export function useAutosizeTextArea({
  ref,
  maxHeight = Number.MAX_SAFE_INTEGER,
  borderWidth = 1,
  dependencies,
}: Props) {
  const originalHeight = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    const currentRef = ref.current;
    const borderAdjustment = borderWidth * 2;

    originalHeight.current ??= currentRef.scrollHeight - borderAdjustment;

    currentRef.style.removeProperty('height');
    const scrollHeight = currentRef.scrollHeight;

    const clampedToMax = Math.min(scrollHeight, maxHeight);
    const clampedToMin = Math.max(clampedToMax, originalHeight.current);

    currentRef.style.height = `${clampedToMin + borderAdjustment}px`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxHeight, ref, ...dependencies]);
}
